/* ============================================================
   PEAR Fleet — Analytics SVG charts (React port of charts.js).
   No chart library; matches the Fleet Console mockup exactly.
   ============================================================ */
import React, { useLayoutEffect, useRef, useState } from "react";

const C = { ink3: "#99a1ae", ink2: "#59616f", grid: "#eef0f3", blue: "#2563eb", red: "#dc2626" };

/** Measure a container's width so SVG charts scale responsively. */
function useWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(600);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => setW(node.clientWidth || 600);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function niceMax(v: number): number {
  if (v <= 0) return 10;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / p;
  const n = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return n * p;
}

export interface Series { color: string; data: number[]; }

export function AreaChartSVG({ series, labels, height = 248, fmt }: { series: Series[]; labels: string[]; height?: number; fmt?: (v: number) => string }) {
  const [ref, w] = useWidth();
  const padL = 52, padR = 16, padT = 14, padB = 30;
  const iw = w - padL - padR, ih = height - padT - padB;
  let max = 0;
  series.forEach((s) => s.data.forEach((v) => (max = Math.max(max, v))));
  max = niceMax(max * 1.1) || 10;
  const xs = (i: number) => padL + (labels.length <= 1 ? iw / 2 : (iw * i) / (labels.length - 1));
  const ys = (v: number) => padT + ih - (v / max) * ih;

  return (
    <div ref={ref} style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ fontFamily: "Geist, sans-serif" }}>
        {[0, 1, 2, 3, 4].map((g) => {
          const yv = (max * g) / 4, y = ys(yv);
          return (
            <g key={g}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke={C.grid} strokeWidth={1} />
              <text x={padL - 10} y={y + 4} textAnchor="end" fontSize={11} fill={C.ink3}>{fmt ? fmt(yv) : Math.round(yv)}</text>
            </g>
          );
        })}
        {labels.map((l, i) => (
          <text key={i} x={xs(i)} y={height - 9} textAnchor="middle" fontSize={11} fill={C.ink3}>{l}</text>
        ))}
        <defs>
          {series.map((s, si) => (
            <linearGradient key={si} id={`ag-${si}-${s.color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        {series.map((s, si) => {
          let line = "";
          s.data.forEach((v, i) => { line += (i ? "L" : "M") + xs(i) + "," + ys(v) + " "; });
          const area = line + `L${xs(s.data.length - 1)},${ys(0)} L${xs(0)},${ys(0)} Z`;
          return (
            <g key={si}>
              <path d={area} fill={`url(#ag-${si}-${s.color.replace("#", "")})`} />
              <path d={line} fill="none" stroke={s.color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
              {s.data.map((v, i) => <circle key={i} cx={xs(i)} cy={ys(v)} r={3} fill="#fff" stroke={s.color} strokeWidth={2} />)}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function BarChartSVG({ data, height = 230, fmt, valueLabels, bw = 54 }: { data: { label: string; value: number; color?: string }[]; height?: number; fmt?: (v: number) => string; valueLabels?: boolean; bw?: number }) {
  const [ref, w] = useWidth();
  const padL = 52, padR = 16, padT = 16, padB = 34;
  const iw = w - padL - padR, ih = height - padT - padB;
  let max = Math.max(1, ...data.map((d) => d.value));
  max = niceMax(max * 1.1) || 10;
  const ys = (v: number) => padT + ih - (v / max) * ih;
  const n = Math.max(1, data.length), slot = iw / n, barW = Math.min(bw, slot * 0.6);
  return (
    <div ref={ref} style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ fontFamily: "Geist, sans-serif" }}>
        {[0, 1, 2, 3, 4].map((g) => {
          const yv = (max * g) / 4, y = ys(yv);
          return (
            <g key={g}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke={C.grid} strokeWidth={1} />
              <text x={padL - 10} y={y + 4} textAnchor="end" fontSize={11} fill={C.ink3}>{fmt ? fmt(yv) : Math.round(yv)}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const x = padL + slot * i + slot / 2 - barW / 2, y = ys(d.value), bh = Math.max(0, padT + ih - y);
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={bh} rx={7} fill={d.color || C.blue} />
              {valueLabels && <text x={x + barW / 2} y={y - 7} textAnchor="middle" fontSize={11} fontWeight={700} fill={C.ink2}>{fmt ? fmt(d.value) : d.value}</text>}
              <text x={padL + slot * i + slot / 2} y={height - 12} textAnchor="middle" fontSize={11.5} fill={C.ink2} fontWeight={500}>{d.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function LineChartSVG({ data, height = 236, color = C.red }: { data: { label: string; value: number }[]; height?: number; color?: string }) {
  const [ref, w] = useWidth();
  const padL = 40, padR = 16, padT = 14, padB = 30;
  const iw = w - padL - padR, ih = height - padT - padB;
  let max = Math.max(1, ...data.map((d) => d.value));
  max = niceMax(max * 1.2) || 5;
  const xs = (i: number) => padL + (data.length <= 1 ? iw / 2 : (iw * i) / (data.length - 1));
  const ys = (v: number) => padT + ih - (v / max) * ih;
  let line = "";
  data.forEach((d, i) => { line += (i ? "L" : "M") + xs(i) + "," + ys(d.value) + " "; });
  const gid = `lg-${color.replace("#", "")}`;
  return (
    <div ref={ref} style={{ width: "100%" }}>
      <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ fontFamily: "Geist, sans-serif" }}>
        {[0, 1, 2, 3, 4].map((g) => {
          const yv = (max * g) / 4, y = ys(yv);
          return (
            <g key={g}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke={C.grid} strokeWidth={1} />
              <text x={padL - 9} y={y + 4} textAnchor="end" fontSize={11} fill={C.ink3}>{Math.round(yv)}</text>
            </g>
          );
        })}
        {data.map((d, i) => <text key={i} x={xs(i)} y={height - 9} textAnchor="middle" fontSize={10.5} fill={C.ink3}>{d.label}</text>)}
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.18} />
            <stop offset="100%" stopColor={color} stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <path d={line + `L${xs(data.length - 1)},${ys(0)} L${xs(0)},${ys(0)} Z`} fill={`url(#${gid})`} />
        <path d={line} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" />
        {data.map((d, i) => <circle key={i} cx={xs(i)} cy={ys(d.value)} r={3.5} fill="#fff" stroke={color} strokeWidth={2} />)}
      </svg>
    </div>
  );
}

export function Donut({ data, center, sub, size = 190 }: { data: { value: number; color: string }[]; center: string; sub: string; size?: number }) {
  const cx = size / 2, cy = size / 2, r = size / 2 - 10, sw = 22;
  const total = data.reduce((a, d) => a + d.value, 0) || 1;
  let ang = -Math.PI / 2;
  const arcs = data.map((d, i) => {
    const frac = d.value / total, a2 = ang + frac * Math.PI * 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(ang), y1 = cy + r * Math.sin(ang);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    ang = a2;
    return <path key={i} d={`M${x1},${y1} A${r},${r} 0 ${large} 1 ${x2},${y2}`} fill="none" stroke={d.color} strokeWidth={sw} strokeLinecap="butt" />;
  });
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ fontFamily: "Geist, sans-serif" }}>
      {arcs}
      <text x={cx} y={cy - 2} textAnchor="middle" fontSize={26} fontWeight={800} fill="#101729">{center}</text>
      <text x={cx} y={cy + 16} textAnchor="middle" fontSize={11} fontWeight={600} fill={C.ink3}>{sub}</text>
    </svg>
  );
}

export function HBars({ data, fmt }: { data: { label: string; value: number; color?: string }[]; fmt?: (v: number) => string }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <>
      {data.map((d, i) => (
        <div className="hbar-row" key={i}>
          <div className="hbar-lbl">{d.label}</div>
          <div className="hbar-track"><div className="hbar-fill" style={{ width: `${((d.value / max) * 100).toFixed(1)}%`, background: d.color || C.blue }} /></div>
          <div className="hbar-val">{fmt ? fmt(d.value) : d.value}</div>
        </div>
      ))}
    </>
  );
}
